import { useTheme } from "../hooks/use-theme";
import React, { ComponentProps, forwardRef, useMemo } from "react";
import { Pressable, StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import Icon, { IconName } from "./Icon";

type IconButtonProps = {
    style?: StyleProp<ViewStyle>;
    icon: IconName;
    iconColor?: string;
    containerColor?: string;
    size?: number | "small" | "medium" | "large";
    mode?: "default" | "contained" | "outlined";
    testID?: string;
} & Pick<ComponentProps<typeof Pressable>, "onPress" | "hitSlop" | "disabled">;

const IconButton = forwardRef<View, IconButtonProps>(
    (
        {
            style,
            icon,
            size = 24,
            mode = "default",
            containerColor,
            iconColor,
            onPress,
            disabled,
            hitSlop,
            testID,
        },
        ref,
    ) => {
        const styles = useStyles();
        const { colors } = useTheme();
        const realSize =
            size === "small" ? 24 : size === "medium" ? 44 : size === "large" ? 60 : size;
        const [moreStyle, iconSize] = useMemo(() => {
            const moreStyle: ViewStyle = {
                height: realSize,
                width: realSize,
            };
            let iconSize = realSize;
            if (mode == "contained") {
                moreStyle["overflow"] = "hidden";
                moreStyle["backgroundColor"] = containerColor || colors.surfaceVariant;
                moreStyle["borderColor"] = containerColor || colors.surfaceVariant;
                moreStyle["borderRadius"] = realSize / 2;
                moreStyle["borderWidth"] = 1;
                iconSize = Math.round(realSize * 0.5);
            }
            if (mode == "outlined") {
                moreStyle["borderColor"] = containerColor || colors.primary;
                moreStyle["backgroundColor"] = containerColor || colors.background;
                moreStyle["borderRadius"] = realSize / 2;
                moreStyle["borderWidth"] = 2;
                iconSize = Math.round(realSize * 0.5);
            }
            if (disabled) {
                moreStyle["opacity"] = 0.5;
                moreStyle["borderColor"] = colors.accentLighter;
            }
            return [moreStyle, iconSize];
        }, [
            colors.accentLighter,
            colors.background,
            colors.primary,
            colors.surfaceVariant,
            containerColor,
            disabled,
            mode,
            realSize,
        ]);

        return (
            // collapsable must be set to false to be able to use measureInWindow ( see use-dropdown )
            // see: https://github.com/facebook/react-native/issues/29712
            <Pressable
                ref={ref}
                collapsable={false}
                style={[styles.container, moreStyle, style]}
                onPress={onPress}
                disabled={disabled}
                testID={testID}
                hitSlop={hitSlop}
            >
                <Icon
                    name={icon}
                    size={iconSize}
                    style={{ opacity: disabled ? 0.5 : 1 }}
                    color={disabled ? colors.onSurfaceDisabled : iconColor || colors.primary}
                />
            </Pressable>
        );
    },
);

IconButton.displayName = "IconButton";

const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                },
            }),
        [],
    );
};

export default IconButton;
